import { type ComponentProps, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import {
  Links,
  Meta,
  NavLink,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from '@remix-run/react';
import type { LayoutComponent } from '@remix-run/react/dist/routeModules';
import { withSentry } from '@sentry/remix';

import { ToastRackProvider } from '@shieldpay/bumblebee/toast-rack';

import {
  AmplifyConfigSchema,
  amplifyInit,
} from '@shieldpay/authentication-ui/amplify-config';
import { BumblebeeProvider } from '@shieldpay/theme-provider-ui';
import { shieldpayTheme } from '@shieldpay/theme-provider-ui/theme';
import { useIsHydrated } from '@shieldpay/utilities-ui/use-is-hydrated';
import { parseLocale } from '@shieldpay/utility-functions-ui/parse-locale';
import { useServerOnlyNonce } from '@shieldpay/wheeljack/remix/nonce-context.ts';
import { SentrySchema } from '@shieldpay/wheeljack/remix/sentry.ts';

import { messages } from './i18n';
import { createMeta } from './utilities/create-meta';

import { DevErrorBoundary } from '~/components/error-boundary/error-boundary-dev';
import { ProdErrorBoundary } from '~/components/error-boundary/error-boundary-prod';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({
    locale: parseLocale(request),
    cognitoConfig: AmplifyConfigSchema.parse(process.env),
    SENTRY_ENV: SentrySchema.parse(process.env),
  });
};

export const links: LinksFunction = () => [
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
    media: 'all',
  },
];

export const meta = createMeta('Shieldpay');

export function Layout({ children }: ComponentProps<LayoutComponent>) {
  const data = useRouteLoaderData<typeof loader>('root');

  const cognitoConfig = data?.cognitoConfig;
  const locale = data?.locale || 'en-GB';

  useMemo(() => {
    cognitoConfig && amplifyInit(cognitoConfig);
  }, [cognitoConfig]);

  const nonce = useServerOnlyNonce();

  const isHydrated = useIsHydrated();

  return (
    <html lang={locale} data-hydrated={isHydrated}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />

        <Meta />
        <Links />
      </head>
      <BumblebeeProvider NavLink={NavLink}>
        <IntlProvider locale={locale} messages={messages}>
          <body className={shieldpayTheme}>
            <ToastRackProvider>
              {children}
              <ScrollRestoration nonce={nonce} />
              {/* This is how we hand off the values from the server to the client. */}
              <script
                dangerouslySetInnerHTML={{
                  __html: `window.__SENTRY_VARS__ = ${JSON.stringify(data?.SENTRY_ENV)}`,
                }}
                nonce={nonce || ''}
              />
              <Scripts nonce={nonce} />
            </ToastRackProvider>
          </body>
        </IntlProvider>
      </BumblebeeProvider>
    </html>
  );
}

export default withSentry(Outlet);

export const ErrorBoundary = import.meta.env.DEV
  ? DevErrorBoundary
  : ProdErrorBoundary;
